/**
 * Holy Shape – Pre-Order Button Logic v21
 * v21: Much more visible pre-order indicator.
 *      - Replaces the tiny 8px corner dot with a "PRE-ORDER" tag tucked into
 *        the top-right corner of the size button (small but readable).
 *      - Adds a 1.5px camel border around the whole button via inset shadow,
 *        so the button itself reads as different from a glance.
 *      - Kept the bottom accent line as a fallback signal.
 * v20: Badge only the Size picker (skip Color, Material, etc.). Color swatches
 *      are inherently small and the dot crowded them; the size grid is where
 *      customers actually scan. Matches any option whose name contains "size"
 *      (case-insensitive) — so 'Size', 'Bra Size', 'Size (UK)' all qualify.
 * v19: Fix TypeError when product.options contains objects (modern Shopify
 *      theme format: [{name:'Color',values:[...]}, {name:'Size',values:[...]}]
 *      rather than the legacy ['Color', 'Size']). v18 crashed on first iteration
 *      so no picker decoration ran at all.
 * v18: Variant-picker decoration — robust matcher + clip-safe indicator.
 *      - Now matches pickers by data-variant-id when present (most accurate),
 *        then by fuzzy text (handles screen-reader prefixes like "Size: XS/S").
 *      - Indicator is now a small camel-coloured dot INSIDE the button at the
 *        top-right corner, so it survives buttons with overflow:hidden.
 *      - Also adds a thin bottom accent line for clearer at-a-glance signal.
 *      - Logs a one-line diagnostic on first decorate so we can debug from the
 *        browser console: '[HS Preorder] picker decorated: N elements'.
 * v17: Initial variant-picker decoration.
 * v16: Customer-side Lebanon-market guard added. Non-Lebanon visitors
 *      (e.g. Qatar via Shopify Markets) skip ALL pre-order behavior.
 * v15: System is now Beirut-location + Lebanon-market EXCLUSIVE.
 *      Product page inventory check now uses Beirut-only API data
 *      (Liquid total inventory is fallback only).
 * v14: Stock-aware on EVERY page — uses inventory data from the
 *      /api/preorder-variants endpoint (which fetches Beirut stock).
 * v13: Works on every page (quick-add, recommendations, etc.)
 * v12: Inventory from Liquid (product.js hides it on this store).
 * v11: Only show pre-order when stock = 0.
 * v10: Match the right Add-to-Cart form when page has multiple ones.
 */
(function () {
  'use strict';

  // ── Lebanon-market guard ─────────────────────────────────────────────
  // This system is exclusive to the Lebanon market. Customers shopping
  // from other countries (via Shopify Markets) get normal storefront
  // behavior — no pre-order UI, no injected properties, no COD hiding.
  function isLebanonMarket() {
    var country = '';
    try {
      if (window.Shopify) {
        if (Shopify.country) country = String(Shopify.country).toUpperCase();
      }
    } catch (e) {}
    // If we couldn't detect the country, default to Lebanon (the home market)
    if (!country) return true;
    return country === 'LB';
  }

  if (!isLebanonMarket()) {
    // eslint-disable-next-line no-console
    console.log('[HS Preorder] Skipped — not Lebanon market (Shopify.country=' + (window.Shopify && Shopify.country) + ')');
    return;
  }

  var preorderData     = window.__HS_PREORDER || {};
  var preorderKeys     = Object.keys(preorderData);
  var product          = null;
  var currentVariantId = null;
  var _applying        = false;
  var _burstTimer      = null;
  var _styleInjected   = false;

  // ── Inject CSS once ───────────────────────────────────────────────────────
  function injectStyles() {
    if (_styleInjected) return;
    var style = document.createElement('style');
    style.textContent = [
      'button[data-hs-preorder="true"] > span:not(.hs-po-label),',
      'button[data-hs-preorder="true"] > [data-add-to-cart-text] {',
      '  display: none !important;',
      '}',
      '.hs-po-label {',
      '  display: inline !important;',
      '  font-size: inherit;',
      '  font-weight: inherit;',
      '}',
    ].join('\n');
    document.head.appendChild(style);
    _styleInjected = true;
  }

  // ── Variant picker decoration ─────────────────────────────────────────────
  // Adds a small Holy Shape-themed "PRE-ORDER" corner badge to any variant
  // picker button (size swatch, color swatch, dropdown option) whose underlying
  // variant is currently pre-order. Buy-button behavior is unchanged — this is
  // purely a hint for the picker so customers don't have to click through every
  // option to find pre-order ones.
  var _pickerStyleInjected = false;
  function injectPickerStyles() {
    if (_pickerStyleInjected) return;
    var s = document.createElement('style');
    s.textContent = [
      // The picker element gets data-hs-po-variant="1"
      '[data-hs-po-variant="1"] {',
      '  position: relative !important;',
      // Camel border around the whole button — overrides theme via inset shadow
      // (works even on borderless buttons / rounded-corner cards)
      '  box-shadow: inset 0 0 0 1.5px #b8865e !important;',
      '}',
      // "PRE-ORDER" corner tag — sits INSIDE the button top-right so it
      // survives parent overflow:hidden (common on rounded picker buttons).
      '[data-hs-po-variant="1"]::after {',
      '  content: "PRE-ORDER";',
      '  position: absolute;',
      '  top: 0;',
      '  right: 0;',
      '  background: linear-gradient(135deg, #d9b08c 0%, #b8865e 100%);',
      '  color: #ffffff;',
      '  font-family: inherit;',
      '  font-size: 7.5px;',
      '  font-weight: 800;',
      '  letter-spacing: 0.6px;',
      '  text-transform: uppercase;',
      '  padding: 2.5px 5px 2.5px 6px;',
      '  border-bottom-left-radius: 8px;',
      '  line-height: 1;',
      '  white-space: nowrap;',
      '  pointer-events: none;',
      '  z-index: 3;',
      '  box-shadow: -1px 1px 3px rgba(184, 134, 94, .35);',
      '}',
      // Thin accent line along the bottom edge — kept as a fallback signal
      // in case the corner tag is hidden by theme CSS.
      '[data-hs-po-variant="1"]:not(.hs-po-no-accent)::before {',
      '  content: "";',
      '  position: absolute;',
      '  left: 8%;',
      '  right: 8%;',
      '  bottom: 3px;',
      '  height: 2px;',
      '  border-radius: 1px;',
      '  background: linear-gradient(90deg, transparent 0%, #b8865e 20%, #d9b08c 50%, #b8865e 80%, transparent 100%);',
      '  pointer-events: none;',
      '  z-index: 1;',
      '}',
      // Optional inline label class (kept for future use)
      '.hs-po-variant-badge {',
      '  display: inline-block;',
      '  background: linear-gradient(135deg, #d9b08c 0%, #b8865e 100%);',
      '  color: #fff;',
      '  font-size: 9px;',
      '  font-weight: 700;',
      '  letter-spacing: 0.6px;',
      '  text-transform: uppercase;',
      '  padding: 2px 6px;',
      '  border-radius: 9px;',
      '  margin-left: 4px;',
      '  line-height: 1;',
      '}',
    ].join('\n');
    document.head.appendChild(s);
    _pickerStyleInjected = true;
  }

  // Extract only the visible text of an element, ignoring screen-reader prefixes
  // (e.g. .visually-hidden / .sr-only / [aria-hidden] / inputs).
  function visibleText(el) {
    if (!el) return '';
    var clone = el.cloneNode(true);
    clone.querySelectorAll(
      '.visually-hidden, .sr-only, .screen-reader-text, .visuallyhidden, ' +
      '[aria-hidden="true"], input, select, .hs-po-variant-badge'
    ).forEach(function (n) { n.parentNode && n.parentNode.removeChild(n); });
    return (clone.textContent || '').replace(/\s+/g, ' ').trim();
  }

  // Does this element's visible text reference the given option value?
  // Handles "Size: XS/S", "XS/S Size", trailing colon, etc.
  function textMentionsValue(text, value) {
    if (!text || !value) return false;
    var t = text.toLowerCase().trim();
    var v = String(value).toLowerCase().trim();
    if (!t || !v) return false;
    if (t === v) return true;
    // Equal after stripping common label prefixes ("Size: ", "Color: ")
    var stripped = t.replace(/^[a-z][a-z\s]*:\s*/i, '').trim();
    if (stripped === v) return true;
    // The value appears as a standalone token
    var re = new RegExp('(?:^|[\\s:\\/])' + v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '(?:$|[\\s:\\/])');
    return re.test(t);
  }

  // Normalize an option name regardless of which Shopify response shape we got:
  //   legacy:  'Size'
  //   modern:  { name: 'Size', values: [...] }
  function optionNameOf(o) {
    if (!o) return '';
    if (typeof o === 'string') return o;
    if (typeof o.name === 'string') return o.name;
    if (typeof o.option === 'string') return o.option;
    return '';
  }

  // Try every reasonable way to find the picker control for a given option
  // value (and the variant it would resolve to). Returns the clickable element.
  function findPickerEl(optionName, value, optionIndex, variantId) {
    var lowName = optionNameOf(optionName).toLowerCase();
    var trimVal = String(value || '').trim();
    if (!trimVal) return null;

    // 0) HIGHEST PRIORITY — picker elements that already declare the variant ID
    if (variantId) {
      var vidSel = [
        '[data-variant-id="' + variantId + '"]',
        '[data-product-variant-id="' + variantId + '"]',
        'a[href*="variant=' + variantId + '"]',
      ];
      for (var v = 0; v < vidSel.length; v++) {
        var ve = document.querySelector(vidSel[v]);
        if (ve) {
          // Prefer a clickable ancestor (label or button) over a raw <input>
          var clickable = ve.closest('label, button, .swatch, [role="button"]');
          return clickable || ve;
        }
      }
    }

    // 1) Native radio inputs whose value matches and whose name relates to the option
    var radios = document.querySelectorAll(
      'input[type="radio"][value="' + cssEscape(trimVal) + '"]'
    );
    for (var i = 0; i < radios.length; i++) {
      var r = radios[i];
      var n = (r.name || '').toLowerCase();
      if (!lowName || n.indexOf(lowName) !== -1 || n === '' || n.indexOf('option') !== -1) {
        var lab = r.closest('label');
        if (lab) return lab;
        if (r.id) {
          var external = document.querySelector('label[for="' + cssEscape(r.id) + '"]');
          if (external) return external;
        }
        return r;
      }
    }

    // 2) Buttons or labels with data attributes
    var dataSelectors = [
      '[data-option-value="' + cssEscape(trimVal) + '"]',
      '[data-value="' + cssEscape(trimVal) + '"]',
      '[data-variant-option-value="' + cssEscape(trimVal) + '"]',
      '[data-option-' + (optionIndex + 1) + '="' + cssEscape(trimVal) + '"]',
    ];
    for (var k = 0; k < dataSelectors.length; k++) {
      var el = document.querySelector(dataSelectors[k]);
      if (el) return el.closest('label, button, .swatch, [role="button"]') || el;
    }

    // 3) Fallback — find any button/label whose VISIBLE text mentions the value.
    //    Strips screen-reader prefixes like "Size: " or "Variant: ".
    //    We score candidates so the tightest match wins.
    var allClickables = document.querySelectorAll('button, label, .swatch, [role="button"]');
    var best = null;
    var bestScore = 0;
    for (var j = 0; j < allClickables.length; j++) {
      var c = allClickables[j];
      // Skip our own buy button to avoid re-decorating it
      if (c.getAttribute('data-hs-preorder') === 'true') continue;
      if (c.getAttribute('data-hs-po-variant') === '1' && c.__hsPoValue !== trimVal) continue;
      var txt = visibleText(c);
      if (!textMentionsValue(txt, trimVal)) continue;
      // Score: prefer exact match, then ancestor that mentions the option name
      var score = 1;
      if (txt.toLowerCase() === trimVal.toLowerCase()) score += 5;
      var fs = c.closest('fieldset, .product-form__input, .variant-picker, .product-options, ' +
                         '[class*="variant-input"], [class*="option-selector"], [class*="swatch"]');
      if (fs && lowName) {
        var legendText = '';
        var legend = fs.querySelector('legend, .product-form__label, .form__label, label');
        if (legend) legendText = legend.textContent.toLowerCase();
        if (legendText.indexOf(lowName) !== -1) score += 3;
      }
      if (score > bestScore) { best = c; bestScore = score; }
    }
    if (best) return best;

    return null;
  }

  function cssEscape(s) {
    s = String(s);
    if (window.CSS && CSS.escape) return CSS.escape(s);
    return s.replace(/[!"#$%&'()*+,.\/:;<=>?@\[\\\]^`{|}~]/g, '\\$&');
  }

  function markPickerEl(el, isPreorder, variant, value) {
    if (!el) return false;
    if (!isPreorder) {
      el.removeAttribute('data-hs-po-variant');
      el.removeAttribute('title');
      el.__hsPoValue = null;
      return false;
    }
    el.setAttribute('data-hs-po-variant', '1');
    el.__hsPoValue = String(value || '');
    var po = _globalPreorderList && _globalPreorderList[String(variant.id)];
    if (po && po.eta) {
      el.setAttribute('title', 'Pre-Order — arrives ' + formatDate(po.eta));
    } else {
      el.setAttribute('title', 'Pre-Order item');
    }
    return true;
  }

  // Returns the variant matching the current selection but with ONE option swapped.
  function findHypotheticalVariant(currentOpts, optionIndex, newValue) {
    if (!product || !product.variants) return null;
    var target = currentOpts.slice();
    target[optionIndex] = newValue;
    return product.variants.find(function (v) {
      var vo = v.options || [];
      for (var i = 0; i < target.length; i++) {
        if (target[i] === undefined) continue;
        if (String(vo[i]) !== String(target[i])) return false;
      }
      return true;
    });
  }

  function decorateVariantPickers() {
    if (!product || !_globalPreorderList) return;
    if (!product.options || product.options.length === 0) return;
    if (!product.variants || product.variants.length === 0) return;

    injectPickerStyles();

    var current = findVariant(currentVariantId);
    if (!current) current = product.variants[0];
    if (!current) return;
    var currentOpts = current.options || [];

    var totalMarked = 0;
    var totalEvaluated = 0;
    var totalPreorder = 0;
    var missingMatches = [];

    // Clear any leftover badges on options we no longer decorate (e.g. Color
    // from a previous version that badged everything). Without this they'd
    // stay until the user navigates away.
    document.querySelectorAll('[data-hs-po-variant="1"]').forEach(function (el) {
      el.__hsPoValue = null;
      el.removeAttribute('data-hs-po-variant');
      el.removeAttribute('title');
    });

    product.options.forEach(function (optionRaw, optionIndex) {
      var optionName = optionNameOf(optionRaw);
      // Only badge the Size picker. Color swatches are small and would get
      // crowded by the dot; size is the grid customers actually scan.
      if (!/size/i.test(optionName)) return;

      var seen = {};
      var uniqueVals = [];
      product.variants.forEach(function (v) {
        var val = (v.options || [])[optionIndex];
        if (val === undefined || val === null || seen[val]) return;
        seen[val] = true;
        uniqueVals.push(val);
      });

      uniqueVals.forEach(function (value) {
        totalEvaluated++;
        var hypo = findHypotheticalVariant(currentOpts, optionIndex, value);
        if (!hypo) return;
        var po = globalShouldShowPreorder(hypo.id);
        if (po) totalPreorder++;
        var el = findPickerEl(optionName, value, optionIndex, hypo.id);
        if (!el) {
          if (po) missingMatches.push(optionName + '=' + value);
          return;
        }
        if (markPickerEl(el, !!po, hypo, value)) totalMarked++;
      });
    });

    // One-line diagnostic so we can verify it's running and why
    try {
      console.log('[HS Preorder] picker decorated: ' + totalMarked + ' of ' +
        totalPreorder + ' pre-order options (out of ' + totalEvaluated +
        ' total)' +
        (missingMatches.length ? ' — UNMATCHED: ' + missingMatches.join(', ') : ''));
    } catch (e) {}
  }

  // Re-decorate when the theme re-renders the picker (e.g. on variant change).
  // Throttled — themes can fire many mutations during a single switch.
  var _pickerObs = null;
  var _pickerObsLast = 0;
  function watchPickerForReRender() {
    if (_pickerObs) return;
    _pickerObs = new MutationObserver(function () {
      var now = Date.now();
      if (now - _pickerObsLast < 200) return;
      _pickerObsLast = now;
      // requestAnimationFrame ensures the theme's DOM updates have settled
      requestAnimationFrame(decorateVariantPickers);
    });
    _pickerObs.observe(document.body, { childList: true, subtree: true, attributes: true,
      attributeFilter: ['data-option-value', 'aria-checked', 'aria-pressed', 'class'] });
  }

  // ── Date formatter ────────────────────────────────────────────────────────
  function formatDate(dateStr) {
    if (!dateStr) return '';
    var p = dateStr.split('-');
    var months = ['January','February','March','April','May','June',
                  'July','August','September','October','November','December'];
    return months[parseInt(p[1], 10) - 1] + ' ' + parseInt(p[2], 10) + ', ' + p[0];
  }

  // ── Find the MAIN product form ─────────────────────────────────────────────
  // The page has multiple cart forms — quick-add widgets, hidden duplicates,
  // bundle forms, etc. The REAL one has:
  //   1. A variant ID matching our preorderData keys, AND
  //   2. An actual visible add-to-cart button
  function findMainProductForm() {
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    for (var i = 0; i < forms.length; i++) {
      var idEl = forms[i].querySelector('input[name="id"], select[name="id"]');
      if (!idEl || preorderKeys.indexOf(String(idEl.value)) === -1) continue;

      // Must have a submit button (skips hidden/duplicate forms)
      var btn = forms[i].querySelector('[name="add"]') ||
                forms[i].querySelector('#AddToCart')    ||
                forms[i].querySelector('[data-add-to-cart]') ||
                forms[i].querySelector('button[type="submit"]');
      if (!btn) continue;

      // Skip if the button is for "Add selected items" (bundle button)
      var txt = (btn.textContent || '').toLowerCase();
      if (txt.indexOf('selected') !== -1) continue;

      return forms[i];
    }
    return null;
  }

  // ── Button finder (now scoped to main product form) ───────────────────────
  function findBtn() {
    var form = findMainProductForm();
    if (form) {
      return form.querySelector('[name="add"]') ||
             form.querySelector('#AddToCart')    ||
             form.querySelector('[data-add-to-cart]') ||
             form.querySelector('button[type="submit"]');
    }
    return null;
  }

  // ── Get variant ID from main product form ─────────────────────────────────
  function getFormVariantId() {
    var form = findMainProductForm();
    if (!form) return null;
    var input = form.querySelector('input[name="id"], select[name="id"]');
    return input ? input.value : null;
  }

  // ── Stock check ────────────────────────────────────────────────────────────
  // This system is exclusive to the Beirut location. We treat a variant as
  // "pre-order" only if:
  //   - it has an active PO, AND
  //   - Beirut inventory is <= 0 (or unknown)
  // We prefer the API value (_globalPreorderList[id].inventory) because it
  // is Beirut-only. The Liquid-injected `data.inventory` is the TOTAL across
  // all locations, so we only fall back to it when the API hasn't loaded yet.
  function isVariantOutOfStock(variantId) {
    var id = String(variantId);

    // Prefer Beirut-only inventory from the API (most accurate)
    if (_globalPreorderList && _globalPreorderList[id]) {
      var po = _globalPreorderList[id];
      if (po.inventory !== null && po.inventory !== undefined) {
        return po.inventory <= 0;
      }
      // PO is active but Beirut inventory unknown → default to out of stock
      return true;
    }

    // Fallback to Liquid data (total across all locations) while API is loading
    var data = preorderData[id];
    if (!data) return false;
    if (!data.inventory_managed) return false;
    var qty = Number(data.inventory);
    if (isNaN(qty)) return false;
    return qty <= 0;
  }

  // ── Core: stamp pre-order state onto the button ───────────────────────────
  function applyPreorderState(variantId) {
    if (_applying) return;
    _applying = true;

    var id   = String(variantId);
    var data = preorderData[id];
    var hasActivePo = !!(data && (data.active === true || data.active === 'true'));
    var outOfStock  = isVariantOutOfStock(id);

    // Pre-order behavior ONLY when PO is active AND the variant is out of stock.
    // If there is still stock the customer should buy normally.
    var isPreorder = hasActivePo && outOfStock;

    var btn = findBtn();
    if (!btn) { _applying = false; return; }

    // Remove previous notice & hidden inputs
    var oldNotice = document.getElementById('hs-preorder-notice');
    if (oldNotice) oldNotice.remove();

    var form = btn.closest('form');
    if (form) {
      // Remove any previously-injected pre-order line item properties
      form.querySelectorAll('input[data-hs-preorder-property]').forEach(function (el) {
        el.remove();
      });
    }

    if (isPreorder) {
      injectStyles();

      btn.removeAttribute('disabled');
      btn.setAttribute('data-hs-preorder', 'true');
      btn.style.opacity        = '1';
      btn.style.pointerEvents  = 'auto';
      btn.style.cursor         = 'pointer';

      var label = 'Pre-Order – Arrives ' + formatDate(data.eta);

      var ourLabel = btn.querySelector('.hs-po-label');
      if (!ourLabel) {
        ourLabel = document.createElement('span');
        ourLabel.className = 'hs-po-label';
        btn.insertBefore(ourLabel, btn.firstChild);
      }
      ourLabel.textContent = label;

      // Hide direct text nodes
      Array.from(btn.childNodes).forEach(function (n) {
        if (n.nodeType === 3 && n.textContent.trim().length > 0) {
          n.textContent = '';
        }
      });

      // Customer-facing notice (under the button on product page)
      var notice = document.createElement('div');
      notice.id = 'hs-preorder-notice';
      notice.style.cssText = 'font-size:0.78em;color:#888;margin:0.5rem 0 0;text-align:center;line-height:1.5;';
      notice.innerHTML =
        '&#9888;&#65039; <strong>Pre-Order item.</strong> ' +
        'Estimated arrival date is approximate &mdash; may arrive a few days earlier or later.<br>' +
        'Online payment only (no Cash on Delivery).<br>' +
        '<span style="opacity:.8">If your order is significantly delayed, please contact us on WhatsApp.</span>';
      if (form) { form.appendChild(notice); }
      else       { btn.insertAdjacentElement('afterend', notice); }

      if (form) {
        var formattedEta = formatDate(data.eta);

        // 1) HIDDEN property — for our backend webhook + Shopify Function (COD hider)
        var hiddenInp = document.createElement('input');
        hiddenInp.type = 'hidden';
        hiddenInp.name = 'properties[_preorder_eta]';
        hiddenInp.value = data.eta;
        hiddenInp.setAttribute('data-hs-preorder-property', '1');
        form.appendChild(hiddenInp);

        // 2) VISIBLE property — shown to customer in cart, checkout & order email.
        //    Property name (no leading underscore) is what makes it visible.
        var visibleInp = document.createElement('input');
        visibleInp.type = 'hidden';  // hidden in form, BUT visible as a line item property in checkout
        visibleInp.name = 'properties[Pre-Order]';
        visibleInp.value =
          'Estimated arrival around ' + formattedEta +
          ' — the date is approximate and may arrive a few days before or after.' +
          ' Online payment only (no Cash on Delivery).' +
          ' If your order is significantly delayed, please contact us on WhatsApp.';
        visibleInp.setAttribute('data-hs-preorder-property', '1');
        form.appendChild(visibleInp);
      }
    } else {
      // ── CLEANUP ───────────────────────────────────────────────────────
      btn.removeAttribute('data-hs-preorder');
      var poLabel = btn.querySelector('.hs-po-label');
      if (poLabel) poLabel.remove();
    }

    _applying = false;
  }

  // ── Burst reapply after variant switch ────────────────────────────────────
  function burstReapply(variantId) {
    currentVariantId = String(variantId);
    if (_burstTimer) clearInterval(_burstTimer);

    applyPreorderState(currentVariantId);
    decorateVariantPickers(); // re-evaluate picker badges for the new context

    var ticks = 0;
    _burstTimer = setInterval(function () {
      applyPreorderState(currentVariantId);
      ticks++;
      if (ticks >= 20) {
        clearInterval(_burstTimer);
        _burstTimer = null;
      }
    }, 150);
  }

  // ── PRIMARY DETECTION: poll the main form's hidden input ──────────────────
  setInterval(function () {
    if (_applying) return;
    var formVid = getFormVariantId();
    if (!formVid) return;
    formVid = String(formVid);

    if (formVid !== currentVariantId) {
      burstReapply(formVid);
    }
  }, 250);

  // ── GUARDIAN: ensure correct state ────────────────────────────────────────
  setInterval(function () {
    if (_applying || _burstTimer) return;
    var cid = currentVariantId;
    if (!cid) return;

    var d = preorderData[cid];
    var isPreorder = !!(d && (d.active === true || d.active === 'true'));
    var btn = findBtn();
    if (!btn) return;

    if (isPreorder && !btn.querySelector('.hs-po-label')) {
      applyPreorderState(cid);
    } else if (!isPreorder && (btn.querySelector('.hs-po-label') || btn.getAttribute('data-hs-preorder') === 'true')) {
      applyPreorderState(cid);
    }
  }, 500);

  // ── Variant-change events ─────────────────────────────────────────────────
  document.addEventListener('variant:changed', function (e) {
    var v = e.detail && e.detail.variant;
    if (v && String(v.id) !== currentVariantId) burstReapply(v.id);
  });

  document.addEventListener('variantChange', function (e) {
    var v = e.detail && (e.detail.variant || e.detail);
    if (v && v.id && String(v.id) !== currentVariantId) burstReapply(v.id);
  });

  // ── Variant lookup helper ─────────────────────────────────────────────────
  function findVariant(id) {
    if (!product) return null;
    return product.variants.find(function (v) { return String(v.id) === String(id); });
  }

  // ── Boot ──────────────────────────────────────────────────────────────────
  function init() {
    var isProductPage = window.location.pathname.indexOf('/products/') !== -1;

    if (isProductPage && preorderKeys.length > 0) {
      // Product page: use Liquid-injected data (accurate, stock-aware)
      var parts = window.location.pathname.split('/products/');
      var handle = parts[1].split('?')[0].split('/')[0];
      fetch('/products/' + handle + '.js')
        .then(function (r) { return r.json(); })
        .then(function (p) {
          product = p;
          var formVid = getFormVariantId();
          var urlId = new URLSearchParams(window.location.search).get('variant');
          var initialId = formVid || urlId;
          var variant = initialId ? findVariant(initialId) : null;
          if (!variant) variant = p.variants[0];
          if (variant) burstReapply(variant.id);
          // Mark pre-order options in the picker so the customer doesn't have
          // to click each one to find out which are pre-order.
          decorateVariantPickers();
          watchPickerForReRender();
        })
        .catch(function (e) { console.warn('[HS Preorder]', e); });
    }

    // On EVERY page (including collection, search, recommendations widgets):
    // fetch the global pre-order variant list and intercept add-to-cart actions.
    initGlobalIntercept();
  }

  // ══════════════════════════════════════════════════════════════════════════
  // GLOBAL ADD-TO-CART INTERCEPT (works on all pages, all add-to-cart paths)
  // ══════════════════════════════════════════════════════════════════════════

  var _globalPreorderList = null;  // { variantId: { eta } } - fetched from our API

  function initGlobalIntercept() {
    var apiUrl = window.__HS_PREORDER_API;
    if (!apiUrl) return;

    fetch(apiUrl, { cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        _globalPreorderList = data.variants || {};
        if (Object.keys(_globalPreorderList).length === 0) return;

        // 1) Scan & relabel all add-to-cart buttons currently on the page
        scanAndApplyPreorderUI();
        // Variant picker badges depend on API data — decorate now in case
        // product.js finished loading before the API call did.
        decorateVariantPickers();

        // 2) Re-scan when the DOM changes (lazy-loaded recommendations, etc.)
        var lastScan = 0;
        new MutationObserver(function () {
          var now = Date.now();
          if (now - lastScan < 500) return;  // throttle
          lastScan = now;
          scanAndApplyPreorderUI();
        }).observe(document.body, { childList: true, subtree: true });

        // 3) Intercept ALL form submissions to /cart/add (catches any path)
        document.addEventListener('submit', interceptFormSubmit, true);

        // 4) Intercept fetch() calls to /cart/add (AJAX quick-adds)
        wrapFetchForCartAdd();
      })
      .catch(function (e) { console.warn('[HS Preorder] API fetch failed:', e); });
  }

  // Re-label every add-to-cart button on the page whose form's variant is a pre-order
  function scanAndApplyPreorderUI() {
    if (!_globalPreorderList) return;
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    for (var i = 0; i < forms.length; i++) {
      labelFormIfPreorder(forms[i]);
    }
  }

  // Should this variant currently be treated as pre-order?
  // PO active AND out of stock at Beirut (inventory <= 0 or unknown)
  function globalShouldShowPreorder(variantId) {
    if (!_globalPreorderList) return null;
    var po = _globalPreorderList[String(variantId)];
    if (!po) return null;
    if (po.inventory !== null && po.inventory !== undefined && po.inventory > 0) return null;
    return po;
  }

  function labelFormIfPreorder(form) {
    var idEl = form.querySelector('input[name="id"], select[name="id"]');
    if (!idEl) return;
    var vid = String(idEl.value);
    var po = globalShouldShowPreorder(vid);
    if (!po) {
      // Not pre-order (no PO OR in stock) — remove our markers if any
      var btn = form.querySelector('[name="add"], button[type="submit"]');
      if (btn && btn.getAttribute('data-hs-preorder-global') === '1') {
        btn.removeAttribute('data-hs-preorder-global');
        btn.removeAttribute('data-hs-preorder');
        var lbl = btn.querySelector('.hs-po-label');
        if (lbl) lbl.remove();
      }
      // Also remove injected hidden inputs
      form.querySelectorAll('input[data-hs-preorder-property]').forEach(function (el) { el.remove(); });
      return;
    }

    // PRE-ORDER variant — re-label button + inject properties
    injectStyles();
    var btn = form.querySelector('[name="add"], button[type="submit"]');
    if (!btn) return;
    if (btn.getAttribute('data-hs-preorder-global') === '1') return;  // already done

    btn.setAttribute('data-hs-preorder-global', '1');
    btn.setAttribute('data-hs-preorder', 'true');

    var lbl = btn.querySelector('.hs-po-label');
    if (!lbl) {
      lbl = document.createElement('span');
      lbl.className = 'hs-po-label';
      btn.insertBefore(lbl, btn.firstChild);
    }
    lbl.textContent = 'Pre-Order – Arrives ' + formatDate(po.eta);

    // Hide direct text nodes (Debutify-style buttons)
    Array.from(btn.childNodes).forEach(function (n) {
      if (n.nodeType === 3 && n.textContent.trim().length > 0) n.textContent = '';
    });

    // Inject hidden properties so the cart line item is marked as pre-order
    ensureProperty(form, '_preorder_eta', po.eta);
    ensureProperty(form, 'Pre-Order',
      'Estimated arrival around ' + formatDate(po.eta) +
      ' — the date is approximate and may arrive a few days before or after.' +
      ' Online payment only (no Cash on Delivery).' +
      ' If your order is significantly delayed, please contact us on WhatsApp.');
  }

  function ensureProperty(form, key, value) {
    var existing = form.querySelector('input[name="properties[' + key + ']"]');
    if (existing) { existing.value = value; return; }
    var inp = document.createElement('input');
    inp.type = 'hidden';
    inp.name = 'properties[' + key + ']';
    inp.value = value;
    inp.setAttribute('data-hs-preorder-property', '1');
    form.appendChild(inp);
  }

  // Intercept synchronous form POSTs to /cart/add (legacy themes)
  function interceptFormSubmit(e) {
    var form = e.target;
    if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;
    labelFormIfPreorder(form);  // ensure properties are in form data before submit
  }

  // Wrap window.fetch so AJAX add-to-cart requests get pre-order properties added
  function wrapFetchForCartAdd() {
    if (!window.fetch || window.fetch.__hs_wrapped) return;
    var origFetch = window.fetch;
    window.fetch = function (input, init) {
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        if (url.indexOf('/cart/add') !== -1 && init && init.body) {
          init.body = augmentCartAddBody(init.body);
        }
      } catch (e) { /* fall through */ }
      return origFetch.apply(this, arguments);
    };
    window.fetch.__hs_wrapped = true;
  }

  // Add pre-order properties to a /cart/add request body
  function augmentCartAddBody(body) {
    if (!_globalPreorderList) return body;

    // JSON body
    if (typeof body === 'string' && body.trim().startsWith('{')) {
      try {
        var parsed = JSON.parse(body);
        var items = parsed.items || (parsed.id ? [parsed] : null);
        if (!items) return body;
        items.forEach(function (item) {
          var vid = String(item.id || '');
          var po = globalShouldShowPreorder(vid);
          if (!po) return;
          item.properties = item.properties || {};
          if (!item.properties['_preorder_eta']) {
            item.properties['_preorder_eta'] = po.eta;
            item.properties['Pre-Order'] =
              'Estimated arrival around ' + formatDate(po.eta) +
              ' — the date is approximate and may arrive a few days before or after.' +
              ' Online payment only (no Cash on Delivery).' +
              ' If your order is significantly delayed, please contact us on WhatsApp.';
          }
        });
        return JSON.stringify(parsed);
      } catch (e) { return body; }
    }

    // FormData / URLSearchParams body
    if (body instanceof FormData || body instanceof URLSearchParams) {
      var vid = String(body.get('id') || '');
      var po = globalShouldShowPreorder(vid);
      if (!po) return body;
      if (!body.has('properties[_preorder_eta]')) {
        body.append('properties[_preorder_eta]', po.eta);
        body.append('properties[Pre-Order]',
          'Estimated arrival around ' + formatDate(po.eta) +
          ' — the date is approximate and may arrive a few days before or after.' +
          ' Online payment only (no Cash on Delivery).' +
          ' If your order is significantly delayed, please contact us on WhatsApp.');
      }
    }

    return body;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
